import {useEffect, useRef} from "react";
import { useLegendState as useState } from "@/hooks/useLegendState";
import {X, ArrowLeft, FileText, Image as ImageIcon, Video, File, Loader2, ExternalLink} from "lucide-react";
import {Dialog, DialogContent, DialogHeader, DialogTitle} from "@/components/ui/dialog";
import {Button} from "@/components/ui/button";
import {ScrollArea} from "@/components/ui/scroll-area";
import {Textarea} from "@/components/ui/textarea";
import {apiRequest} from "@/lib/queryClient";
import {useToast} from "@/hooks/use-toast";

interface FilePreviewProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    file: {
        id: string;
        filename: string;
        url: string;
    } | null;
    files?: Array<{
        id: string;
        filename: string;
        url: string;
    }>;
    onFileSelect?: (fileId: string) => void;
    onOpenFile?: (url: string) => void;
}

function FilePreview({open, onOpenFile, onOpenChange, file, files = [], onFileSelect}: FilePreviewProps) {
    const {toast} = useToast();
    const [currentFile, setCurrentFile] = useState(file);
    const [fileUrl, setFileUrl] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [previewMode, setPreviewMode] = useState<"list" | "preview">("preview");
    const [textContent, setTextContent] = useState<string>("");
    const [extractingText, setExtractingText] = useState(false);
    const [fileType, setFileType] = useState<"text" | "pdf" | "image" | "video" | "other">("other");
    const [isDocument, setIsDocument] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);

    // Update current file when prop changes
    useEffect(() => {
        if (file) {
            setCurrentFile(file);
            setPreviewMode("preview");
        }
    }, [file]);

    // Refresh file URL when current file changes
    useEffect(() => {
        if (currentFile && open) {
            refreshFileUrl();
        }
    }, [currentFile?.id, open]);

    const refreshFileUrl = async () => {
        if (!currentFile) return;
        console.log("[FilePreview] Refreshing URL for file:", currentFile);
        console.log("[FilePreview] File ID:", currentFile.id);
        console.log("[FilePreview] File ID type:", typeof currentFile.id);
        console.log("[FilePreview] File ID length:", currentFile.id?.length);

        setLoading(true);
        setError(null);
        try {
            // Clean the file ID (remove any whitespace or special characters)
            const cleanFileId = String(currentFile.id).trim();

            if (!cleanFileId) {
                throw new Error("File ID is empty");
            }

            // Get fresh URL from API (this will generate a new signed URL)
            const response = await apiRequest("GET", `/api/files/${encodeURIComponent(cleanFileId)}`);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({error: "Unknown error"}));
                const errorMessage = errorData.error || `HTTP ${response.status}`;

                // If refresh fails, try using the original URL as fallback
                if (currentFile.url && currentFile.url.trim()) {
                    console.warn("[FilePreview] Failed to refresh URL, using original URL as fallback");
                    setFileUrl(currentFile.url);
                    determineFileType(currentFile.filename, currentFile.url);
                    toast({
                        title: "Warning",
                        description: "Using cached file URL. The link may expire soon.",
                        variant: "default",
                    });
                    return;
                }

                throw new Error(errorMessage);
            }

            const data = await response.json();
            console.log("[FilePreview] Received file data:", data);

            if (data.url) {
                setFileUrl(data.url);
                determineFileType(currentFile.filename, data.url);
            } else {
                // Fallback to original URL if available
                if (currentFile.url && currentFile.url.trim()) {
                    console.warn("[FilePreview] No URL in response, using original URL");
                    setFileUrl(currentFile.url);
                    determineFileType(currentFile.filename, currentFile.url);
                } else {
                    throw new Error("No URL in response");
                }
            }
        } catch (err: any) {
            console.error("[FilePreview] Error refreshing file URL:", err);
            const errorMessage = err.message || "Failed to load file";

            // Last resort: try using original URL
            if (currentFile.url && currentFile.url.trim()) {
                console.warn("[FilePreview] Using original URL as last resort");
                setFileUrl(currentFile.url);
                determineFileType(currentFile.filename, currentFile.url);
                toast({
                    title: "Warning",
                    description: "Using cached file URL. If it doesn't work, the file may have been deleted.",
                    variant: "default",
                });
            } else {
                setError(errorMessage);
                toast({
                    title: "Error",
                    description: `Failed to load file: ${errorMessage}. The file may have been deleted or the ID is incorrect.`,
                    variant: "destructive",
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const determineFileType = (filename: string, url: string) => {
        const ext = filename.toLowerCase().split(".").pop() || "";
        const mimeType = url.includes("?") ? "" : ""; // We'll detect from response if needed

        // Document types that can have text extracted
        const documentExts = ["doc", "docx", "xls", "xlsx", "ppt", "pptx", "odt", "ods", "odp"];

        if (["pdf"].includes(ext)) {
            setFileType("pdf");
            setIsDocument(false);
            extractPdfText(url);
        } else if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(ext)) {
            setFileType("image");
            setIsDocument(false);
        } else if (["mp4", "webm", "ogg", "mov", "avi", "mkv"].includes(ext)) {
            setFileType("video");
            setIsDocument(false);
        } else if (documentExts.includes(ext)) {
            // Document types - extract text from server
            setFileType("text");
            setIsDocument(true);
            extractDocumentText();
        } else if (["txt", "md", "json", "xml", "csv", "log", "js", "ts", "tsx", "jsx", "html", "css"].includes(ext)) {
            setFileType("text");
            setIsDocument(false);
            fetchTextContent(url);
        } else {
            setFileType("other");
            setIsDocument(false);
        }
    };

    const fetchTextContent = async (url: string) => {
        setExtractingText(true);
        try {
            const response = await fetch(url, {credentials: "include"});
            if (!response.ok) {
                throw new Error("Failed to fetch file content");
            }
            const text = await response.text();
            setTextContent(text);
        } catch (err: any) {
            console.error("Error fetching text content:", err);
            setTextContent("Failed to load file content. You can open it in a new tab to view.");
        } finally {
            setExtractingText(false);
        }
    };

    const extractDocumentText = async () => {
        if (!currentFile) return;

        setExtractingText(true);
        setTextContent(""); // Clear previous content
        
        try {
            // Call server endpoint to extract text from document
            const response = await apiRequest("GET", `/api/files/${currentFile.id}/text`);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({error: "Unknown error"}));
                throw new Error(errorData.error || `HTTP ${response.status}`);
            }

            const data = await response.json();
            if (data.text && data.text.trim().length > 0) {
                setTextContent(data.text);
            } else {
                // No text extracted - offer to open in new tab
                setTextContent("No text content could be extracted from this document. Click 'Open in New Tab' to view the full document.");
                // Automatically open in new tab after a short delay if extraction fails
                setTimeout(() => {
                    if (fileUrl || currentFile.url) {
                        handleOpenInNewTab();
                    }
                }, 2000);
            }
        } catch (err: any) {
            console.error("Error extracting document text:", err);
            const errorMessage = err.message || "Failed to extract text";
            
            // If extraction fails, try to open in new tab automatically
            setTextContent(`Failed to extract text: ${errorMessage}\n\nOpening document in a new tab...`);
            
            // Automatically open in new tab after showing error message
            setTimeout(() => {
                if (fileUrl || currentFile.url) {
                    handleOpenInNewTab();
                } else {
                    // If no URL available, refresh it first
                    refreshFileUrl().then(() => {
                        if (fileUrl) {
                            handleOpenInNewTab();
                        }
                    });
                }
            }, 1500);
        } finally {
            setExtractingText(false);
        }
    };

    const extractPdfText = async (url: string) => {
        setExtractingText(true);
        try {
            // First, try to get text from server endpoint if available
            try {
                const response = await apiRequest("GET", `/api/files/${currentFile?.id}/text`);
                const data = await response.json();
                if (data.text) {
                    setTextContent(data.text);
                    setExtractingText(false);
                    return;
                }
            } catch (serverError) {
                // Server endpoint not available, try client-side extraction
                console.log("Server PDF text extraction not available, trying client-side");
            }

            // Try client-side extraction with pdf.js if available
            const response = await fetch(url, {credentials: "include"});
            if (!response.ok) {
                throw new Error("Failed to fetch PDF");
            }

            // Check if pdf.js is available
            if (typeof window !== "undefined" && (window as any).pdfjsLib) {
                const pdfjsLib = (window as any).pdfjsLib;
                const arrayBuffer = await response.arrayBuffer();
                const pdf = await pdfjsLib.getDocument({data: arrayBuffer}).promise;
                let fullText = "";

                for (let i = 1; i <= pdf.numPages; i++) {
                    const page = await pdf.getPage(i);
                    const textContent = await page.getTextContent();
                    const pageText = textContent.items
                        .map((item: any) => item.str)
                        .join(" ");
                    fullText += pageText + "\n\n";
                }

                setTextContent(fullText);
            } else {
                // No text extraction available - will show iframe preview instead
                setTextContent("");
            }
        } catch (err: any) {
            console.error("Error extracting PDF text:", err);
            // Will fall back to iframe preview
            setTextContent("");
        } finally {
            setExtractingText(false);
        }
    };

    const handleOpenInNewTab = () => {
        const urlToOpen = fileUrl || currentFile?.url || "";
        
        if (!urlToOpen) {
            toast({
                title: "Error",
                description: "No file URL available to open.",
                variant: "destructive",
            });
            return;
        }

        // Use a more direct approach to avoid browser extension interference
        // Create a temporary link and trigger it programmatically
        try {
            // Method 1: Use window.open with a small delay to avoid extension interference
            const newWindow = window.open(urlToOpen, "_blank", "noopener,noreferrer");
            if (newWindow) {
                newWindow.location.href = urlToOpen;
                // If onOpenFile callback is provided, also call it
                if (onOpenFile) {
                    onOpenFile(urlToOpen);
                }
                return;
            }
        } catch (err) {
            console.warn("window.open failed, trying alternative method:", err);
        }

        // Method 2: Create and click an anchor element
        try {
            const link = document.createElement("a");
            link.href = urlToOpen;
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            link.style.display = "none";
            
            // Add to DOM temporarily
            document.body.appendChild(link);
            
            // Use a small timeout to avoid extension interference
            setTimeout(() => {
                try {
                    link.click();
                    // Clean up after a short delay
                    setTimeout(() => {
                        if (link.parentNode) {
                            document.body.removeChild(link);
                        }
                    }, 100);
                } catch (clickErr) {
                    console.error("Link click failed:", clickErr);
                    if (link.parentNode) {
                        document.body.removeChild(link);
                    }
                    // Fallback: try window.open again
                    window.open(urlToOpen, "_blank", "noopener,noreferrer");
                }
            }, 10);
            
            // Also call callback if provided
            if (onOpenFile) {
                onOpenFile(urlToOpen);
            }
        } catch (err: any) {
            console.error("Error opening file in new tab:", err);
            // Last resort: direct window.open
            window.open(urlToOpen, "_blank", "noopener,noreferrer");
            if (onOpenFile) {
                onOpenFile(urlToOpen);
            }
        }
    };

    const handleFileClick = (fileId: string) => {
        const selectedFile = files.find(f => f.id === fileId);
        if (selectedFile) {
            setCurrentFile(selectedFile);
            setPreviewMode("preview");
            if (onFileSelect) {
                onFileSelect(fileId);
            }
        }
    };

    if (!currentFile) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
                <DialogHeader className="sr-only">
                    <DialogTitle>File Preview: {currentFile?.filename || "File"}</DialogTitle>
                </DialogHeader>
                <div className="flex items-center justify-between border-b px-6 py-4">
                    <div className="flex items-center gap-4">
                        {previewMode === "preview" && files.length > 1 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setPreviewMode("list")}
                            >
                                <ArrowLeft className="h-4 w-4 mr-2"/>
                                Back to Files
                            </Button>
                        )}
                        <div className="flex items-center gap-2">
                            {fileType === "pdf" && <FileText className="h-5 w-5"/>}
                            {fileType === "image" && <ImageIcon className="h-5 w-5"/>}
                            {fileType === "video" && <Video className="h-5 w-5"/>}
                            {fileType === "text" && <FileText className="h-5 w-5"/>}
                            {fileType === "other" && <File className="h-5 w-5"/>}
                            <h2 className="text-lg font-semibold truncate max-w-md">
                                {currentFile.filename}
                            </h2>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleOpenInNewTab}
                        >
                            <ExternalLink className="h-4 w-4 mr-2"/>
                            Open in New Tab
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onOpenChange(false)}
                        >
                            <X className="h-4 w-4"/>
                        </Button>
                    </div>
                </div>

                <div className="flex-1 overflow-hidden">
                    {previewMode === "list" ? (
                        <ScrollArea className="h-full">
                            <div className="p-6">
                                <h3 className="text-lg font-semibold mb-4">Attached Files</h3>
                                <div className="space-y-2">
                                    {files.map((f) => (
                                        <div
                                            key={f.id}
                                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-accent ${
                                                currentFile.id === f.id ? "bg-accent" : ""
                                            }`}
                                            onClick={() => handleFileClick(f.id)}
                                        >
                                            <FileText className="h-5 w-5 text-muted-foreground"/>
                                            <span className="flex-1 truncate">{f.filename}</span>
                                            {currentFile.id === f.id && (
                                                <span className="text-xs text-muted-foreground">Current</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </ScrollArea>
                    ) : (
                        <div className="h-full flex flex-col">
                            {loading ? (
                                <div className="flex-1 flex items-center justify-center">
                                    <div className="text-center">
                                        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2"/>
                                        <p className="text-sm text-muted-foreground">Loading file...</p>
                                    </div>
                                </div>
                            ) : error ? (
                                <div className="flex-1 flex items-center justify-center p-6">
                                    <div className="text-center">
                                        <p className="text-sm text-destructive mb-4">{error}</p>
                                        <Button onClick={refreshFileUrl} variant="outline">
                                            Retry
                                        </Button>
                                    </div>
                                </div>
                            ) : fileUrl ? (
                                <ScrollArea className="flex-1">
                                    <div className="p-6">
                                        {fileType === "text" && (
                                            <div className="space-y-4">
                                                <div className="flex items-center justify-between">
                                                    <h3 className="text-sm font-medium">
                                                        {isDocument ? "Extracted Document Content" : "File Content"}
                                                    </h3>
                                                    <div className="flex items-center gap-2">
                                                        {extractingText && (
                                                            <div
                                                                className="flex items-center gap-2 text-xs text-muted-foreground">
                                                                <Loader2 className="h-3 w-3 animate-spin"/>
                                                                {isDocument ? "Extracting text from document..." : "Loading..."}
                                                            </div>
                                                        )}
                                                        {isDocument && !extractingText && (
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={handleOpenInNewTab}
                                                            >
                                                                <ExternalLink className="h-3 w-3 mr-2"/>
                                                                Open Full Document
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                                {isDocument && !extractingText && textContent && !textContent.includes("Failed to extract") && !textContent.includes("No text content") && (
                                                    <p className="text-xs text-muted-foreground">
                                                        Text extracted from document. Formatting and images are not
                                                        included. Use "Open Full Document" to view the complete file.
                                                    </p>
                                                )}
                                                {isDocument && !extractingText && (textContent.includes("Failed to extract") || textContent.includes("No text content") || textContent.includes("Opening document")) && (
                                                    <div className="border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 bg-yellow-50 dark:bg-yellow-900/20">
                                                        <p className="text-xs text-yellow-800 dark:text-yellow-200 mb-2">
                                                            {textContent.includes("Opening document") 
                                                                ? "Text extraction was not successful. Opening document in a new tab..."
                                                                : "Text extraction was not successful. Click 'Open Full Document' above to view the complete file."}
                                                        </p>
                                                    </div>
                                                )}
                                                <Textarea
                                                    value={textContent || ""}
                                                    readOnly
                                                    className="min-h-[400px] font-mono text-sm"
                                                    placeholder={isDocument ? "Extracting text from document..." : "Loading content..."}
                                                />
                                            </div>
                                        )}

                                        {fileType === "pdf" && (
                                            <div className="space-y-4">
                                                <div className="flex items-center justify-between">
                                                    <h3 className="text-sm font-medium">PDF Preview</h3>
                                                    {extractingText && (
                                                        <div
                                                            className="flex items-center gap-2 text-xs text-muted-foreground">
                                                            <Loader2 className="h-3 w-3 animate-spin"/>
                                                            Extracting text...
                                                        </div>
                                                    )}
                                                </div>
                                                {textContent ? (
                                                    <div className="space-y-4">
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-xs text-muted-foreground">Extracted Text Content</span>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => setTextContent("")}
                                                            >
                                                                Show PDF View
                                                            </Button>
                                                        </div>
                                                        <Textarea
                                                            value={textContent}
                                                            readOnly
                                                            className="min-h-[400px] font-mono text-sm"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="space-y-4">
                                                        <iframe
                                                            ref={iframeRef}
                                                            src={fileUrl}
                                                            className="w-full h-[600px] border rounded-lg"
                                                            title={currentFile.filename}
                                                            onError={() => {
                                                                setError("Failed to load PDF. The file may be corrupted or the URL may have expired.");
                                                            }}
                                                        />
                                                        <div
                                                            className="flex items-center justify-between text-xs text-muted-foreground">
                                                            <span>PDF viewer. If content doesn't load, try opening in a new tab.</span>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={async () => {
                                                                    // Try to extract text
                                                                    if (currentFile) {
                                                                        await extractPdfText(fileUrl);
                                                                    }
                                                                }}
                                                                disabled={extractingText}
                                                            >
                                                                {extractingText ? (
                                                                    <>
                                                                        <Loader2 className="h-3 w-3 mr-2 animate-spin"/>
                                                                        Extracting...
                                                                    </>
                                                                ) : (
                                                                    "Extract Text"
                                                                )}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {fileType === "image" && (
                                            <div className="space-y-4">
                                                <h3 className="text-sm font-medium">Image Preview</h3>
                                                <div className="flex justify-center">
                                                    <img
                                                        ref={imgRef}
                                                        src={fileUrl}
                                                        alt={currentFile.filename}
                                                        className="max-w-full h-auto rounded-lg border"
                                                        onError={() => {
                                                            setError("Failed to load image");
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {fileType === "video" && (
                                            <div className="space-y-4">
                                                <h3 className="text-sm font-medium">Video Preview</h3>
                                                <div className="flex justify-center">
                                                    <video
                                                        src={fileUrl}
                                                        controls
                                                        className="max-w-full rounded-lg border"
                                                        onError={() => {
                                                            setError("Failed to load video");
                                                        }}
                                                    >
                                                        Your browser does not support the video tag.
                                                    </video>
                                                </div>
                                            </div>
                                        )}

                                        {fileType === "other" && (
                                            <div className="space-y-4">
                                                <div className="border rounded-lg p-8 text-center">
                                                    <File className="h-12 w-12 mx-auto mb-4 text-muted-foreground"/>
                                                    <p className="text-sm text-muted-foreground mb-4">
                                                        Preview not available for this file type.
                                                    </p>
                                                    <Button onClick={handleOpenInNewTab} variant="outline">
                                                        <ExternalLink className="h-4 w-4 mr-2"/>
                                                        Open in New Tab
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </ScrollArea>
                            ) : (
                                <div className="flex-1 flex items-center justify-center">
                                    <div className="text-center">
                                        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2"/>
                                        <p className="text-sm text-muted-foreground">Preparing file...</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default FilePreview

